import { Avatar, MenuItem, Select, Stack, Typography, Button } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { ComponentProps, FC } from 'react';
import { useApiQuery } from '../query/hooks';
import { graphql } from '../gql';
import { LoadableSelect } from './LoadableSelect';
import { Add } from '@mui/icons-material';
import { OpportunitiesMemberSelect_ListMembersQuery } from '../gql/graphql';

const opportunitiesMemberSelect_listMembers = graphql(`
	query opportunitiesMemberSelect_listMembers {
		listMember {
			id
			name
			avatar {
				url(width: 40, height: 40)
			}
		}
	}
`);

type MemberSelectProps = ComponentProps<typeof Select<string>> &
	Readonly<{
		loading?: boolean;
		hideAdd?: boolean;
		size: 'medium' | 'small';
	}>;

export const MemberSelect: FC<MemberSelectProps> = ({ value, loading, hideAdd, size, ...props }) => {
	const { data, isLoading } = useApiQuery(opportunitiesMemberSelect_listMembers);

	return (
		<LoadableSelect
			loading={loading || isLoading}
			value={value || '_placeholder'}
			renderValue={(memberId) => {
				if (loading) {
					return <FormattedMessage id={'common.loading'} />;
				}

				const owner = data?.listMember.find((f) => f.id === memberId);

				if (owner) {
					return <MemberItem {...owner} size={size} />;
				}

				return (
					<Button
						variant={'text'}
						color={'secondary'}
						startIcon={<Add />}
						sx={{
							position: 'relative',
							left: -6,
							width: '100%',
							height: 28,
							justifyContent: 'start',
							textTransform: 'none',
							opacity: !hideAdd ? 1 : 0,
						}}
					>
						<FormattedMessage id={'common.member.placeholder'} />
					</Button>
				);
			}}
			variant={'standard'}
			sx={{
				'&::before': {
					content: 'none',
				},
			}}
			IconComponent={() => null}
			{...props}
		>
			<MenuItem value={''}>
				<FormattedMessage id={'widget.owner.empty'} />
			</MenuItem>

			{data?.listMember.map((member) => (
				<MenuItem key={member.id} value={member.id}>
					<MemberItem {...member} size={size} />
				</MenuItem>
			))}
		</LoadableSelect>
	);
};

type MemberItemProps = OpportunitiesMemberSelect_ListMembersQuery['listMember'][number] & { size: 'medium' | 'small' };

const MemberItem: FC<MemberItemProps> = ({ name, avatar, size }) => {
	const sizeCss = size === 'medium' ? '40px' : '32px';

	return (
		<Stack direction={'row'} gap={1.5} alignItems={'center'}>
			<Avatar alt={name} src={avatar?.url ?? undefined} sx={{ width: sizeCss, height: sizeCss }} />
			<Typography fontSize={'14px'}>{name}</Typography>
		</Stack>
	);
};
